import { NavLink } from 'react-router'
import { Button, Icon, Pill, Stack, Text } from '@/design-system'
import { cn } from '@/lib/cn'
import { formatMoney, formatPercent, type Money } from '@/lib/money'
import { Logo } from '@/app/Logo'
import { UserMenu } from './UserMenu'
import { NAV_GROUPS, UTILITY_ITEMS, type NavItem } from './nav-config'
import styles from './Sidebar.module.css'

function Item({ item, onNavigate }: { readonly item: NavItem; readonly onNavigate?: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) => cn(styles.item, isActive && styles.active)}
    >
      <Icon name={item.icon} size={19} className={styles.glyph} />
      <span className={styles.label}>{item.label}</span>
      {item.badge && <Pill tone={item.badge === 'New' ? 'ink' : 'neutral'}>{item.badge}</Pill>}
    </NavLink>
  )
}

export function Sidebar({
  totalBalance,
  changeFraction,
  onNavigate,
  onClose,
}: {
  readonly totalBalance: Money | undefined
  readonly changeFraction: number
  readonly onNavigate?: (() => void) | undefined
  readonly onClose?: (() => void) | undefined
}) {
  return (
    <nav className={styles.sidebar} aria-label="Main">
      <div className={styles.brand}>
        <Logo size={26} />
        {onClose && (
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close menu">
            <Icon name="close" size={16} />
          </button>
        )}
      </div>

      <Stack className={styles.balance} gap={3}>
        <Stack direction="row" justify="between" align="center">
          <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
            Total balance
          </Text>
          <Pill tone="accent">{formatPercent(changeFraction)}</Pill>
        </Stack>
        <Text as="p" variant="heading" weight="bold" tone="onDark" numeric>
          {totalBalance ? formatMoney(totalBalance) : '—'}
        </Text>
        <Button variant="onDark" size="sm" block iconStart="add" to="/add-money">
          Add money
        </Button>
      </Stack>

      <div className={styles.groups}>
        {NAV_GROUPS.map((group) => (
          <div key={group.id} className={styles.group}>
            <Text variant="eyebrow" weight="semibold" tone="tertiary" className={styles.groupLabel}>
              {group.label}
            </Text>
            {group.items.map((item) => (
              <Item key={item.to} item={item} {...(onNavigate ? { onNavigate } : {})} />
            ))}
          </div>
        ))}
      </div>

      <div className={styles.utility}>
        {UTILITY_ITEMS.map((item) => (
          <Item key={item.to} item={item} {...(onNavigate ? { onNavigate } : {})} />
        ))}
      </div>

      <UserMenu onNavigate={onNavigate} />
    </nav>
  )
}
